import { FC, useState } from "react";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subheading, Regular, Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Collapse, DataRow, Spinner, Tag } from "capitalrx-components-2.0/lib/general";

import { Finance } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import styles from "./MedicalClaimsProviderList.module.scss";
import { TransactionRow } from "./TransactionRow";
import { formatDollars } from "capitalrx-utilities";


const voidTag = <Tag className={styles.TagStyle} type="system-high-contrast">Issued</Tag>;
const clearedTag = <Tag className={styles.TagStyle} type="success-high-contrast">Cleared</Tag>;


type PaymentDropdownProps = {
  payment_date?: string;
  isUpcoming: boolean;
  result_rows: ReadonlyArray<DataRow<Finance.MedicalClaimProviderReportList>>;
  total_results: number;
  transaction?: Finance.MedicalPaymentTransactionSchema[];
  loading: boolean;
};

export const PaymentDropdown: FC<PaymentDropdownProps> = ({
  payment_date,
  isUpcoming,
  result_rows,
  total_results,
  transaction,
  loading,
}) => {
  const [expanded, setExpanded] = useState(isUpcoming);
  const { isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const StackOrShelf = isCompressed ? Stack : Shelf;

  const title = isUpcoming ? (
    <StackOrShelf spacing="mini">
      <Subheading>Upcoming Payments</Subheading>
      {!isCompressed && <StackOrShelf.Spacer />}
      <Regular className={styles.TitleText}>
        <span className={styles.Number}>{total_results}</span>
        {total_results === 1 ? " claim" : " claims"}
      </Regular>
    </StackOrShelf>
  ) : (
  <>
    {isCompressed && (
      <Stack spacing="mini" alignment="left">
          <Subheading>Payment Issued: {payment_date ?? "Unscheduled"}</Subheading>
          {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_void && voidTag}
          {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_cleared && clearedTag}
          <Shelf spacing="mini">
            <Semibold>Transaction ID: </Semibold>
            <Body>#{transaction?.[0]?.transaction_id}</Body>
          </Shelf>
          <Shelf spacing="mini">
            <Semibold>Total Paid: </Semibold>
            <Body>{formatDollars(transaction?.[0]?.funding_ledger_amount)}</Body>
          </Shelf>
          <Shelf spacing="mini">
            <Semibold>Type: </Semibold>
            <Body>{transaction?.[0]?.funding_ledger_type}</Body>
          </Shelf>
          <StackOrShelf.Spacer />
          <Regular className={styles.TitleText}>
            <span className={styles.Number}>{total_results}</span>
            {total_results === 1 ? " claim" : " claims"}
          </Regular>
          </Stack>
      )}
      {!isCompressed && (
        <Shelf>
          <Subheading>Payment Issued: {payment_date ?? "Unscheduled"}</Subheading>
          {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_void && voidTag}
          {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_cleared && clearedTag}
          <Shelf spacing="mini">
            <Semibold>Transaction ID: </Semibold>
            <Body>#{transaction?.[0]?.transaction_id}</Body>
          </Shelf>
          <Shelf spacing="mini">
            <Semibold>Total Paid: </Semibold>
            <Body>{formatDollars(transaction?.[0]?.funding_ledger_amount)}</Body>
          </Shelf>
          <Shelf spacing="mini">
            <Semibold>Type: </Semibold>
            <Body>{transaction?.[0]?.funding_ledger_type}</Body>
          </Shelf>
          <StackOrShelf.Spacer />
          <Regular className={styles.TitleText}>
            <span className={styles.Number}>{total_results}</span>
            {total_results === 1 ? " claim" : " claims"}
          </Regular>
        </Shelf>
      )}
    </>
  );

  return (
    <Stack spacing="small">
      <Collapse.Blank.Unpadded
        alignCaret={isCompressed ? "right": "left"}
        title={title}
        expanded={expanded}
        onToggleExpand={() => setExpanded((e) => !e)}
        style={{ padding: 0 }}
        className={styles.CollapseNoPadding}
      >
        {loading && (
          <Stack className={styles.LoadingContainer} alignment="center">
            <Spinner size="large" />
          </Stack>
        )}
        {isCompressed && <div className={isCompressed ? styles.ClaimRow : ""} />}

        {!loading && (
          <Stack style={{ paddingTop: "8px" }}>
            {total_results === 0 && (
              <Shelf><Body>No payments found</Body></Shelf>
            )}
            {total_results > 0 && (
              <Stack spacing="small">
                {!isCompressed && (
                  <Shelf spacing="medium-large" className={styles.TitleRow}>
                    <Stack alignment="left" spacing="mini" style={{ width: "115px" }}>
                      <Shelf spacing="mini">
                        <Regular className={styles.TitleText}>service start</Regular>
                      </Shelf>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <Regular className={styles.TitleText}>member id</Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <Regular className={styles.TitleText}>patient name</Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <Regular className={styles.TitleText}>patient account <span className={styles.Number}>#</span></Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <Shelf spacing="mini">
                        <Regular className={styles.TitleText}>submission date</Regular>
                      </Shelf>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "108px" }}>
                      <Regular className={styles.TitleText}>claim status</Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="right" spacing="mini" style={{ width: "75px" }}>
                      <Regular className={styles.TitleText}>amount paid</Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="right" spacing="mini" style={{ width: "179px" }}>
                      <Regular className={styles.TitleText}>patient responsibility</Regular>
                    </Stack>
                  </Shelf>
                )}

                {/* Data rows */}
                {result_rows.map((row) => (
                  <StackOrShelf spacing="small" key={row.id}>
                    <TransactionRow row={row.data.provider_medical_claim_payment_report} />
                  </StackOrShelf>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Collapse.Blank.Unpadded>
      {!expanded && isCompressed && <div className={styles.ClaimRow} />}
    </Stack>
  );
};
